import * as React from 'react'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { createPromiseManager, cancelPromiseManagers } from '../util/PromiseManager'
import { createStateLens } from '../util/react'
import type { FlowReportStatus } from './FlowReport'
import { type PromiseState, initialPromiseState } from '../util/PromiseState'
import { renderCatch } from '../error/renderCatch'
import type { UserProfile } from '../../../shared/domain/users/types.no-deps'
import type { PopupButtonColor } from 'zaptic-popup-button'
import i18next from 'i18next'

interface Props {
    deps: CommonDeps
    openState: FlowReportStatus
    onBack: () => Promise<void>
    completedBy?: UserProfile
    reportType: 'action' | 'task' | 'training' | 'guide'
}

interface State {
    backState: PromiseState<void>
}

export class OpenStateModal extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    public state: State = {
        backState: initialPromiseState,
    }

    private promiseManagers = {
        back: createPromiseManager(() => this.props.deps, createStateLens(this, 'backState')),
    }

    public componentWillUnmount() {
        cancelPromiseManagers(this.promiseManagers)
    }

    private onBack = () => {
        this.promiseManagers.back.trigger(this.props.onBack)
    }

    private tryRender = () => {
        if (this.props.openState === 'open') return null

        const svgURL =
            this.props.openState === 'cancelled' ? 'unhappy-clipboard.svg' : 'green-circle-with-white-heavy-tick.svg'
        const buttonColour: PopupButtonColor = this.props.openState === 'cancelled' ? 'white' : 'green'

        const discardedMessage =
            this.props.openState === 'cancelled'
                ? i18next.t('appcore:OpenStateModal.allDiscarded')
                : i18next.t('appcore:OpenStateModal.someDiscarded')

        const backLabel = i18next.t('appcore:OpenStateModal.goBack')

        return (
            <zaptic-modal>
                <zaptic-full-page-message image={svgURL} heading={this.heading()}>
                    <div className="AppCore__flow__OpenStateModal__message">
                        <p>{this.message()}</p>
                        <zaptic-spacer vertical-spacing={20}></zaptic-spacer>
                        <p>{discardedMessage}</p>
                    </div>
                    <zaptic-spacer vertical-spacing={20}></zaptic-spacer>
                    {!this.promiseManagers.back.isInProgress() && (
                        <zaptic-popup-button
                            class="AppCore__flow__OpenStateModal__button"
                            color={buttonColour}
                            onClick={this.onBack}
                        >
                            {backLabel}
                        </zaptic-popup-button>
                    )}
                </zaptic-full-page-message>
            </zaptic-modal>
        )
    }

    private reportTypeText(reportType: 'action' | 'task' | 'training' | 'guide') {
        switch (reportType) {
            case 'guide':
                return 'guide'
            case 'task':
                return 'task'
            case 'training':
                return 'training'
            case 'action':
                return 'action'
            default:
                assumeExhaustion(reportType)
                return ''
        }
    }

    private heading() {
        const reportTypeText = this.reportTypeText(this.props.reportType)

        switch (this.props.openState) {
            case 'open':
                return null
            case 'cancelled':
                return i18next.t('appcore:OpenStateModal.heading.cancelled', { reportTypeText })
            case 'completed':
                return i18next.t('appcore:OpenStateModal.heading.completed', { reportTypeText })
            case 'unassigned':
                return i18next.t('appcore:OpenStateModal.heading.unassigned', { reportTypeText })
            default:
                assumeExhaustion(this.props.openState)
                return null
        }
    }

    private message() {
        const reportTypeText = this.reportTypeText(this.props.reportType)

        switch (this.props.openState) {
            case 'open':
                return null
            case 'cancelled':
                return i18next.t('appcore:OpenStateModal.message.cancelled', { reportTypeText })
            case 'completed':
                const completedBy = this.props.completedBy?.name

                if (completedBy != null) {
                    return i18next.t('appcore:OpenStateModal.message.completedBy', { reportTypeText, completedBy })
                }

                return i18next.t('appcore:OpenStateModal.message.completed', { reportTypeText })
            case 'unassigned':
                return i18next.t('appcore:OpenStateModal.message.unassigned', { reportTypeText })
            default:
                assumeExhaustion(this.props.openState)
                return null
        }
    }
}
